'use client';

import type { CompanyCountryCode } from '@/lib/countries';
import { memo, useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import {
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { Building2, Lock, User, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type {
  CompanyHistoryEvent,
  GesellschafterItem,
  RegisterParticipantItem,
  SubsidiaryCompanyItem,
} from '@/lib/backend-api';
import {
  generateCompanySlug,
  isOwnerRole,
  NON_MANAGER_ROLE_CODES,
} from '@/lib/company-utils';
import { flagUrlForCountry } from '@/lib/country-flags';
import { formatSharePercent } from '@/lib/helpers';
import { getPublicStorageUrl } from '@/lib/public-storage-url';
import { trackSignupAndRedirect } from '@/lib/track-signup-click';
import { cn } from '@/lib/utils';
import { useLocale } from '@/hooks/use-locale';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataGrid, DataGridContainer } from '@/components/ui/data-grid';
import { DataGridTable } from '@/components/ui/data-grid-table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { LocaleLink } from '@/components/locale-link';
import {
  calculateAge,
  COMPANY_TYPE_REGEX,
  formatAge,
  PERSON_TYPE_REGEX,
} from './companyDetailUtils';
import { SourceAttribution } from './SourceAttribution';

/**
 * Tabbed card showing company structure: Managers, Beteiligungen (subsidiaries), Gesellschafter.
 *
 * Tabs are conditionally rendered based on data availability; the active tab is kept in sync
 * as async data loads (so a guest who initially only sees "Managers" smoothly switches when
 * subsidiaries arrive). Guests also see a locked Gesellschafter tab as a sign-in prompt when
 * no shareholder data is present.
 *
 * Delegates per-tab rendering to {@link ManagersTabContent}, {@link GesellschafterTabContent}
 * and {@link SubsidiariesTabContent}.
 */
export const PeopleTabbedCard = memo(function PeopleTabbedCard({
  participants,
  gesellschafter,
  hasGesellschafterliste,
  kommanditisten = [],
  showKommanditPercent = true,
  subsidiaries,
  isAuthenticated,
  euId,
  showRegisterAttribution = false,
  country,
  registerCourt,
  registerId,
  capturedAt,
  className,
}: {
  participants: RegisterParticipantItem[];
  gesellschafter: GesellschafterItem[];
  hasGesellschafterliste: boolean;
  kommanditisten?: CompanyHistoryEvent[];
  showKommanditPercent?: boolean;
  subsidiaries: SubsidiaryCompanyItem[];
  isAuthenticated: boolean;
  euId: string;
  showRegisterAttribution?: boolean;
  /** Picks the register credited by the attribution: the Firmenbuch for AT,
   *  else the German Handelsregister (callers hide it for CH). */
  country?: CompanyCountryCode;
  registerCourt?: string | null;
  registerId?: string | null;
  capturedAt?: string | null;
  /** Layout classes from the page (mobile `order-*`, borders) — see MOBILE_ORDER. */
  className?: string;
}) {
  const { t } = useTranslation();

  const managers = useMemo(
    () =>
      participants.filter(
        (p) =>
          (!p.role_code || !NON_MANAGER_ROLE_CODES.has(p.role_code)) &&
          !isOwnerRole(p.role ?? ''),
      ),
    [participants],
  );

  // Participants with owner roles (Kommanditist etc.) that aren't in Gesellschafterliste
  const ownerParticipants = useMemo(
    () => participants.filter((p) => isOwnerRole(p.role ?? '')),
    [participants],
  );

  // CD Kommanditisten that will render as their own owner rows (e.g. a large
  // standalone KG whose limited partners are CD-only, not in the SI). Computed
  // here too so tab visibility + count match what the table actually shows.
  const kommanditistenRows = useMemo(
    () =>
      buildKommanditistenRows(
        gesellschafter,
        ownerParticipants,
        kommanditisten,
        showKommanditPercent,
      ),
    [gesellschafter, ownerParticipants, kommanditisten, showKommanditPercent],
  );
  const ownerRowCount = ownerParticipants.length + kommanditistenRows.length;

  const hasManagers = managers.length > 0;
  const hasGesellschafter =
    (hasGesellschafterliste && gesellschafter.length > 0) || ownerRowCount > 0;
  const hasSubsidiaries = subsidiaries.length > 0;

  const firstAvailableTab = hasManagers
    ? 'managers'
    : hasSubsidiaries
      ? 'subsidiaries'
      : hasGesellschafter
        ? 'gesellschafter'
        : 'managers';

  const [activeTab, setActiveTab] = useState(firstAvailableTab);

  // Keep activeTab in sync when available tabs change (e.g. data loads async)
  useEffect(() => {
    const availableTabs = [
      hasManagers && 'managers',
      hasSubsidiaries && 'subsidiaries',
      hasGesellschafter && 'gesellschafter',
    ].filter(Boolean) as string[];
    if (availableTabs.length > 0 && !availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0]);
    }
  }, [hasManagers, hasGesellschafter, hasSubsidiaries, activeTab]);

  // Show the card when any tab has content, or when guest should see the locked Gesellschafter tab
  const showLockedGesellschafter = !isAuthenticated && !hasGesellschafter;
  if (
    !hasManagers &&
    !hasGesellschafter &&
    !hasSubsidiaries &&
    !showLockedGesellschafter
  ) {
    return null;
  }

  const totalCount =
    managers.length +
    gesellschafter.length +
    ownerRowCount +
    subsidiaries.length;

  return (
    <Card variant="grid" className={className}>
      <CardHeader>
        <div className="flex items-start gap-2 w-full">
          <div className="flex-1">
            <CardTitle as="h2">
              {t('companyDetail.companyStructure', 'Unternehmensstruktur')}
            </CardTitle>
          </div>
          <Badge variant="outline" className="shrink-0 mt-1">
            {totalCount}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0 px-0">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Scrollable on narrow screens so no tab label ("Gesellschafter")
              gets truncated; the scrollbar itself is hidden. */}
          <div className="px-5 pt-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <TabsList
              variant="line"
              size="sm"
              className={cn(
                'w-max min-w-full',
                !isAuthenticated ? 'mb-1' : 'mb-3',
              )}
            >
              {hasManagers && (
                <TabsTrigger
                  value="managers"
                  className="gap-1 text-[10px] sm:text-xs justify-start shrink-0 whitespace-nowrap min-h-[44px] sm:min-h-0"
                >
                  <Users size={13} className="shrink-0" />
                  <span className="truncate">
                    {t('companyDetail.managers')}
                  </span>
                  <Badge
                    variant="secondary"
                    className="ml-0.5 h-4 px-1 text-[9px] shrink-0"
                  >
                    {managers.length}
                  </Badge>
                </TabsTrigger>
              )}
              {hasSubsidiaries && (
                <TabsTrigger
                  value="subsidiaries"
                  className="gap-1 text-[10px] sm:text-xs justify-start shrink-0 whitespace-nowrap min-h-[44px] sm:min-h-0"
                >
                  <Building2 size={13} className="shrink-0" />
                  <span className="truncate">
                    {t('companyDetail.beteiligungen')}
                  </span>
                  <Badge
                    variant="secondary"
                    className="ml-0.5 h-4 px-1 text-[9px] shrink-0"
                  >
                    {subsidiaries.length}
                  </Badge>
                </TabsTrigger>
              )}
              {hasGesellschafter ? (
                <TabsTrigger
                  value="gesellschafter"
                  className="gap-1 text-[10px] sm:text-xs justify-start shrink-0 whitespace-nowrap min-h-[44px] sm:min-h-0"
                >
                  <User size={13} className="shrink-0" />
                  <span className="truncate">
                    {t('companyDetail.gesellschafter')}
                  </span>
                  {gesellschafter.length + ownerRowCount > 0 && (
                    <Badge
                      variant="secondary"
                      className="ml-0.5 h-4 px-1 text-[9px] shrink-0"
                    >
                      {gesellschafter.length + ownerRowCount}
                    </Badge>
                  )}
                </TabsTrigger>
              ) : showLockedGesellschafter ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs text-muted-foreground cursor-not-allowed px-2 py-1.5 shrink-0 whitespace-nowrap min-h-[44px] sm:min-h-0">
                        <Lock size={12} className="shrink-0" />
                        <span className="truncate">
                          {t('companyDetail.gesellschafter')}
                        </span>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t('companyDetail.loginToSeeGesellschafter')}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : null}
            </TabsList>
          </div>

          {/* forceMount keeps every panel in the SSR HTML so the company
              links inside (Gesellschafter / Beteiligungen) are crawlable
              even when another tab is active. Radix doesn't hide
              force-mounted panels itself, so inactive ones are hidden via
              data-[state=inactive]:hidden. */}
          {hasManagers && (
            <TabsContent
              value="managers"
              forceMount
              className="mt-0 data-[state=inactive]:hidden"
            >
              <ManagersTabContent
                managers={managers}
                isAuthenticated={isAuthenticated}
              />
            </TabsContent>
          )}
          {hasGesellschafter && (
            <TabsContent
              value="gesellschafter"
              forceMount
              className="mt-0 data-[state=inactive]:hidden"
            >
              <GesellschafterTabContent
                gesellschafter={gesellschafter}
                ownerParticipants={ownerParticipants}
                kommanditisten={kommanditisten}
                showKommanditPercent={showKommanditPercent}
                isAuthenticated={isAuthenticated}
                euId={euId}
              />
            </TabsContent>
          )}
          {hasSubsidiaries && (
            <TabsContent
              value="subsidiaries"
              forceMount
              className="mt-0 data-[state=inactive]:hidden"
            >
              <SubsidiariesTabContent
                subsidiaries={subsidiaries}
                isAuthenticated={isAuthenticated}
                euId={euId}
              />
            </TabsContent>
          )}
        </Tabs>
        {showRegisterAttribution && (
          <SourceAttribution
            source={country === 'AT' ? 'firmenbuch' : 'handelsregister'}
            registerCourt={registerCourt}
            registerId={registerId}
            capturedAt={capturedAt}
            className="mx-5"
          />
        )}
      </CardContent>
    </Card>
  );
});

/**
 * Table of Geschäftsführer / Vorstand / etc. (excluding owner roles — those go to Gesellschafter).
 * Organization managers (e.g. a Verwaltungs GmbH) that resolve to a known company are linked.
 * The age column is only shown to signed-in users.
 */
function ManagersTabContent({
  managers,
  isAuthenticated,
}: {
  managers: RegisterParticipantItem[];
  isAuthenticated: boolean;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const columns = useMemo<ColumnDef<RegisterParticipantItem>[]>(
    () => [
      {
        id: 'name',
        header: t('common.labels.name'),
        cell: (info) => {
          const p = info.row.original;
          const name =
            [p.title, p.first_name, p.last_name].filter(Boolean).join(' ') ||
            p.organization;
          // When an organization manager (e.g. Verwaltungs GmbH on a GmbH &
          // Co. KG) was resolved to a known company, render it as a link to
          // /company/<slug> just like Subsidiaries / Shareholders do.
          const hasLink =
            !!p.organization &&
            !!p.eu_id &&
            !!p.register_court &&
            !!p.register_type &&
            !!p.register_id;
          if (hasLink && name) {
            return (
              <LocaleLink
                href={generateCompanySlug(
                  {
                    name,
                    sitz: p.city ?? null,
                    registerCourt: p.register_court ?? null,
                    registerType: p.register_type ?? null,
                    registerId: p.register_id ?? null,
                  },
                  locale,
                )}
                className="font-semibold text-xs break-words whitespace-normal text-primary hover:underline"
              >
                {name}
              </LocaleLink>
            );
          }
          return (
            <span className="font-semibold text-xs break-words whitespace-normal">
              {name}
            </span>
          );
        },
        size: 150,
      },
      {
        id: 'role',
        header: t('companyDetail.registerType'),
        cell: (info) => (
          <span className="text-xs break-words whitespace-normal">
            {info.row.original.role || '—'}
          </span>
        ),
        size: 120,
      },
      ...(isAuthenticated
        ? [
            {
              id: 'age',
              header: t('companyDetail.age'),
              cell: (info: { row: { original: RegisterParticipantItem } }) => {
                const p = info.row.original;
                const age = calculateAge(p.date_of_birth) ?? p.age ?? null;
                return (
                  <span className="text-xs text-muted-foreground">
                    {formatAge(age, t('companyDetail.age'))}
                  </span>
                );
              },
              size: 100,
            },
          ]
        : []),
      {
        id: 'city',
        header: t('companyDetail.city'),
        cell: (info) => (
          <span className="text-xs break-words whitespace-normal">
            {info.row.original.city || '—'}
          </span>
        ),
        size: 100,
      },
    ],
    [t, isAuthenticated, locale],
  );

  const table = useReactTable({
    data: managers,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <DataGridContainer
      border={false}
      className="max-h-[400px] overflow-hidden flex flex-col min-w-0 [&_th:first-child]:pl-5 [&_td:first-child]:pl-5 [&_th:last-child]:pr-5 [&_td:last-child]:pr-5"
    >
      <DataGrid<RegisterParticipantItem>
        table={table}
        recordCount={managers.length}
        tableLayout={{
          headerSticky: true,
          headerBorder: true,
          rowBorder: true,
          stripped: false,
          dense: true,
          width: 'auto',
          columnsResizable: false,
          cellBorder: false,
        }}
        tableClassNames={{
          base: 'w-full',
          header: 'text-xs',
          headerRow: 'h-8 !bg-muted',
          body: 'text-xs text-foreground font-normal',
          bodyRow: 'h-auto hover:bg-muted/30 transition-colors',
        }}
      >
        <div className="flex-1 min-h-0 overflow-auto">
          <DataGridTable />
        </div>
      </DataGrid>
    </DataGridContainer>
  );
}

/** Normalize a date (DD.MM.YYYY or YYYY-MM-DD…) to a YYYY-MM-DD comparison key. */
function normDateKey(d: string | undefined | null): string | null {
  if (!d) return null;
  const dot = d.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (dot) return `${dot[3]}-${dot[2]}-${dot[1]}`;
  const iso = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  return d;
}

function participantDisplayName(p: RegisterParticipantItem): string {
  return (
    [p.first_name, p.last_name].filter(Boolean).join(' ') ||
    p.organization ||
    ''
  );
}

/**
 * Build standalone owner rows for CD Kommanditisten, deduped against the
 * shareholders already shown (Gesellschafterliste rows + register
 * participants — e.g. the single Kommanditist a small KG's SI already lists).
 *
 * Used both by the table and by the parent card's tab visibility / count, so
 * the two never disagree. For standalone KGs `showKommanditPercent` is false
 * and the percentage is dropped: the Komplementär's stake isn't in the CD, so
 * a % over the Kommanditeinlage alone would mislead.
 */
function buildKommanditistenRows(
  gesellschafter: GesellschafterItem[],
  ownerParticipants: RegisterParticipantItem[],
  kommanditisten: CompanyHistoryEvent[],
  showKommanditPercent: boolean,
): GesellschafterItem[] {
  const shownNameKeys = new Set<string>([
    ...gesellschafter.map((g) => g.name.toLowerCase()),
    ...ownerParticipants.map((p) => participantDisplayName(p).toLowerCase()),
  ]);
  const shownBirthKeys = new Set<string>(
    [
      ...gesellschafter.map((g) => normDateKey(g.date_of_birth)),
      ...ownerParticipants.map((p) => normDateKey(p.date_of_birth)),
    ].filter((d): d is string => !!d),
  );

  const rows: GesellschafterItem[] = [];
  for (const evt of kommanditisten) {
    const d = evt.data ?? {};
    const isCompany = (d.member_type as string) === 'company';
    const vorname = ((d.vorname as string) ?? '').trim();
    const surname = ((d.name as string) ?? '').trim();
    const fullName = isCompany
      ? surname
      : [vorname, surname].filter(Boolean).join(' ');
    if (!fullName) continue;
    const bd = normDateKey(d.birthdate as string);
    if (bd && shownBirthKeys.has(bd)) continue;
    if (shownNameKeys.has(fullName.toLowerCase())) continue;
    shownNameKeys.add(fullName.toLowerCase());
    if (bd) shownBirthKeys.add(bd);
    rows.push({
      name: fullName,
      type: 'Kommanditist(in)',
      first_name: isCompany ? undefined : vorname || undefined,
      last_name: isCompany ? undefined : surname || undefined,
      city: (d.ort as string) ?? undefined,
      date_of_birth: isCompany
        ? undefined
        : ((d.birthdate as string) ?? undefined),
      share_percent: showKommanditPercent
        ? (d.anteil_percent as number | undefined)
        : undefined,
      shares: undefined,
      total_shares: undefined,
      gesellschafter_eu_id: undefined,
      register_court: undefined,
      register_type: undefined,
      register_id: undefined,
      logo_url: undefined,
      favicon_url: undefined,
      title: undefined,
    } as GesellschafterItem);
  }
  return rows;
}

/**
 * Table of shareholders (Gesellschafter) merged from two sources:
 *   - `gesellschafter`: parsed from the Gesellschafterliste (DK) document
 *   - `ownerParticipants`: Kommanditisten/owners from the register itself
 *
 * When available, each row is enriched with share percent / Einlage / total-Einlage pulled
 * from the CD document's historical "Kommanditisten" events using birthdate-then-name lookups
 * (including nobility-particle-stripped and fuzzy prefix matching for company names).
 *
 * Guests see only the first three rows with the third blurred + a sign-in CTA.
 */
function GesellschafterTabContent({
  gesellschafter,
  ownerParticipants = [],
  kommanditisten = [],
  showKommanditPercent = true,
  isAuthenticated,
  euId,
}: {
  gesellschafter: GesellschafterItem[];
  ownerParticipants?: RegisterParticipantItem[];
  kommanditisten?: CompanyHistoryEvent[];
  // When false (standalone KG), Kommanditist share percentages are hidden — the
  // Komplementär's stake isn't in the CD, so any % over the Kommanditeinlage
  // alone would be misleading. Mirrors the backend's `is_standalone_kg` rule.
  showKommanditPercent?: boolean;
  isAuthenticated: boolean;
  euId: string;
}) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const { locale } = useLocale();

  // Convert owner participants (Kommanditist etc.) to GesellschafterItem format,
  // enriching with Kommanditeinlage data from CD aggregation when available.
  const mergedGesellschafter = useMemo<GesellschafterItem[]>(() => {
    // Normalize date to YYYY-MM-DD for comparison (handles both DD.MM.YYYY and YYYY-MM-DD)
    const normDate = (d: string | undefined | null): string | null => {
      if (!d) return null;
      const dotParts = d.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
      if (dotParts) return `${dotParts[3]}-${dotParts[2]}-${dotParts[1]}`;
      const isoParts = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoParts) return `${isoParts[1]}-${isoParts[2]}-${isoParts[3]}`;
      return d;
    };

    // Strip nobility particles for matching
    const stripNobility = (s: string) =>
      s
        .replace(/\b(von der|von dem|von|zu|zum|zur)\b/gi, '')
        .replace(/\s+/g, ' ')
        .trim();

    // Build lookups from kommanditisten for matching against register participants
    const kommByBirthdate = new Map<string, Record<string, unknown>>();
    const kommByName = new Map<string, Record<string, unknown>>();
    const kommAll: Record<string, unknown>[] = [];
    for (const evt of kommanditisten) {
      const d = evt.data ?? {};
      kommAll.push(d);
      const nd = normDate(d.birthdate as string);
      if (nd) kommByBirthdate.set(nd, d);
      // Use only first word of vorname (skip middle names) + last name for matching
      const firstName = ((d.vorname as string) ?? '').split(' ')[0];
      const fullName = [firstName, d.name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (fullName) kommByName.set(fullName, d);
      // Also index without nobility particles (von Baumbach → Baumbach)
      const stripped = stripNobility(fullName);
      if (stripped && stripped !== fullName) kommByName.set(stripped, d);
      // Also index by just the name field (for companies without vorname)
      const nameOnly = ((d.name as string) ?? '').toLowerCase();
      if (nameOnly && !kommByName.has(nameOnly)) kommByName.set(nameOnly, d);
      const nameStripped = stripNobility(nameOnly);
      if (
        nameStripped &&
        nameStripped !== nameOnly &&
        !kommByName.has(nameStripped)
      )
        kommByName.set(nameStripped, d);
    }

    // Fuzzy name match for companies: participant name must be a substantial prefix of komm name
    const findKommByFuzzyName = (
      displayName: string,
    ): Record<string, unknown> | undefined => {
      const lower = displayName.toLowerCase();
      const exact = kommByName.get(lower);
      if (exact) return exact;
      // Prefix match: the shorter string must be at least 70% of the longer one
      // to avoid "Schaeffler" matching "Schaeffler Holding, LP"
      for (const d of kommAll) {
        const kommName = ((d.name as string) ?? '').toLowerCase();
        if (!kommName) continue;
        const shorter = Math.min(lower.length, kommName.length);
        const longer = Math.max(lower.length, kommName.length);
        if (shorter / longer < 0.5) continue;
        if (kommName.startsWith(lower) || lower.startsWith(kommName)) return d;
      }
      return undefined;
    };

    const fromParticipants: GesellschafterItem[] = ownerParticipants.map(
      (p) => {
        const name =
          [p.first_name, p.last_name].filter(Boolean).join(' ') ||
          p.organization ||
          '';
        const pBirthNorm = normDate(p.date_of_birth);
        const kommMatch =
          (pBirthNorm ? kommByBirthdate.get(pBirthNorm) : undefined) ??
          kommByName.get(name.toLowerCase()) ??
          kommByName.get(stripNobility(name.toLowerCase())) ??
          findKommByFuzzyName(name);

        return {
          name,
          title: p.title ?? undefined,
          first_name: p.first_name ?? undefined,
          last_name: p.last_name ?? undefined,
          type: p.role ?? '',
          city: p.city ?? undefined,
          date_of_birth: p.date_of_birth ?? undefined,
          share_percent: showKommanditPercent
            ? (kommMatch?.anteil_percent as number | undefined)
            : undefined,
          shares: showKommanditPercent
            ? (kommMatch?.einlage as number | undefined)
            : undefined,
          total_shares: showKommanditPercent
            ? (kommMatch?.total_einlage as number | undefined)
            : undefined,
          gesellschafter_eu_id: undefined,
          register_court: undefined,
          register_type: undefined,
          register_id: undefined,
          logo_url: undefined,
          favicon_url: undefined,
        } as GesellschafterItem;
      },
    );
    // Avoid duplicates: if a name already exists in gesellschafter, skip
    const existingNames = new Set(
      gesellschafter.map((g) => g.name.toLowerCase()),
    );
    const unique = fromParticipants.filter(
      (p) => !existingNames.has(p.name.toLowerCase()),
    );

    // Also enrich existing gesellschafter items that lack share data
    const enriched = gesellschafter.map((g) => {
      if (!showKommanditPercent) return g;
      if (g.share_percent != null || g.shares != null) return g;
      const gBirthNorm = normDate(g.date_of_birth);
      const kommMatch =
        (gBirthNorm ? kommByBirthdate.get(gBirthNorm) : undefined) ??
        kommByName.get(g.name.toLowerCase()) ??
        kommByName.get(stripNobility(g.name.toLowerCase())) ??
        findKommByFuzzyName(g.name);
      if (!kommMatch) return g;
      return {
        ...g,
        share_percent: kommMatch.anteil_percent as number | undefined,
        shares: kommMatch.einlage as number | undefined,
        total_shares: kommMatch.total_einlage as number | undefined,
      };
    });

    // Render CD Kommanditisten as their own owner rows (deduped against what's
    // already shown). Shared with the parent card's tab visibility / count.
    const fromKommanditisten = buildKommanditistenRows(
      gesellschafter,
      ownerParticipants,
      kommanditisten,
      showKommanditPercent,
    );

    return [...enriched, ...unique, ...fromKommanditisten];
  }, [gesellschafter, ownerParticipants, kommanditisten, showKommanditPercent]);

  const sortedGesellschafter = useMemo<GesellschafterItem[]>(() => {
    const toSharePercent = (item: GesellschafterItem): number | null => {
      if (item.share_percent != null) return item.share_percent;
      if (
        item.shares != null &&
        item.total_shares != null &&
        item.total_shares > 0
      ) {
        return (item.shares / item.total_shares) * 100;
      }
      return null;
    };
    return [...mergedGesellschafter].sort((a, b) => {
      const aPercent = toSharePercent(a);
      const bPercent = toSharePercent(b);
      if (aPercent == null && bPercent == null) return 0;
      if (aPercent == null) return 1;
      if (bPercent == null) return -1;
      return bPercent - aPercent;
    });
  }, [mergedGesellschafter]);

  const columns = useMemo<ColumnDef<GesellschafterItem>[]>(
    () => [
      {
        id: 'name',
        header: t('companyDetail.gesellschafterName'),
        cell: (info) => {
          const g = info.row.original;
          const hasLink = g.gesellschafter_eu_id && g.register_court;
          const typeLabel = g.type ?? '';
          const isPersonType =
            PERSON_TYPE_REGEX.test(typeLabel) ||
            (!COMPANY_TYPE_REGEX.test(typeLabel) && !!g.date_of_birth);
          const displayName =
            isPersonType && (g.first_name || g.last_name)
              ? [g.title, g.first_name, g.last_name].filter(Boolean).join(' ')
              : g.name;
          const LeadingIcon = isPersonType ? User : Building2;
          const iconUrl = !isPersonType
            ? getPublicStorageUrl(g.logo_url) ||
              getPublicStorageUrl(g.favicon_url)
            : null;
          return (
            <div className="flex items-start gap-1.5">
              {iconUrl ? (
                <img
                  src={iconUrl}
                  alt={`${displayName} Logo`}
                  className="h-3.5 w-3.5 shrink-0 mt-0.5 rounded-sm object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    (
                      e.target as HTMLImageElement
                    ).nextElementSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              <LeadingIcon
                className={`h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5${iconUrl ? ' hidden' : ''}`}
              />
              {hasLink ? (
                <LocaleLink
                  href={generateCompanySlug(
                    {
                      name: g.name,
                      registerCourt: g.register_court,
                      registerType: g.register_type ?? undefined,
                      registerId: g.register_id ?? undefined,
                    },
                    locale,
                  )}
                  className="text-xs font-medium hover:underline break-words whitespace-normal"
                  onClick={(e) => e.stopPropagation()}
                >
                  {displayName}
                </LocaleLink>
              ) : (
                <span className="text-xs font-medium break-words whitespace-normal">
                  {displayName}
                </span>
              )}
            </div>
          );
        },
        size: 150,
      },
      {
        id: 'type',
        header: t('companyDetail.gesellschafterType'),
        cell: (info) => (
          <span className="text-xs break-words whitespace-normal">
            {info.row.original.type || '—'}
          </span>
        ),
        size: 120,
      },
      ...(isAuthenticated
        ? [
            {
              id: 'age',
              header: t('companyDetail.age'),
              cell: (info: { row: { original: GesellschafterItem } }) => {
                const g = info.row.original;
                const age = calculateAge(g.date_of_birth) ?? null;
                return (
                  <span className="text-xs text-muted-foreground">
                    {formatAge(age, t('companyDetail.age'))}
                  </span>
                );
              },
              size: 100,
            },
          ]
        : []),
      {
        id: 'city',
        header: t('companyDetail.city'),
        cell: (info: { row: { original: GesellschafterItem } }) => (
          <span className="text-xs break-words whitespace-normal">
            {info.row.original.city || '—'}
          </span>
        ),
        size: 100,
      },
      {
        id: 'shares',
        header: t('companyDetail.gesellschafterShares'),
        cell: (info) => {
          const g = info.row.original;
          if (g.share_percent != null)
            return (
              <span className="text-xs">
                {formatSharePercent(g.share_percent)}
              </span>
            );
          if (
            g.shares != null &&
            g.total_shares != null &&
            g.total_shares > 0
          ) {
            return (
              <span className="text-xs">
                {formatSharePercent((g.shares / g.total_shares) * 100)}
              </span>
            );
          }
          if (g.shares != null)
            return <span className="text-xs">{g.shares}</span>;
          return <span className="text-xs text-muted-foreground">—</span>;
        },
        size: 100,
      },
    ],
    [t, isAuthenticated],
  );

  // MUST be referentially stable across renders: react-table queues an
  // auto-reset microtask whenever `data` identity changes, so an inline
  // .slice() here re-renders → new array → reset → … an infinite microtask
  // loop that hard-freezes the tab (2026-07-16 company-detail freeze).
  const visibleData = useMemo(
    () =>
      isAuthenticated ? sortedGesellschafter : sortedGesellschafter.slice(0, 3),
    [isAuthenticated, sortedGesellschafter],
  );
  const hasMore = !isAuthenticated && sortedGesellschafter.length > 2;

  const table = useReactTable({
    data: visibleData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (sortedGesellschafter.length === 0) {
    if (!isAuthenticated) {
      return (
        <div className="px-5 py-3 text-xs text-muted-foreground text-center">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              trackSignupAndRedirect(pathname, 'gesellschafter', euId);
            }}
            className="text-primary hover:underline"
          >
            {t(
              'companyDetail.registerToSeeShareholders',
              'Sign in to see shareholders',
            )}
          </a>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="relative">
      <DataGridContainer
        border={false}
        className={cn(
          'overflow-hidden flex flex-col min-w-0 [&_th:first-child]:pl-5 [&_td:first-child]:pl-5 [&_th:last-child]:pr-5 [&_td:last-child]:pr-5',
          isAuthenticated ? 'max-h-[320px]' : '',
          !isAuthenticated &&
            '[&_tbody_tr:nth-child(3)]:blur-[5px] [&_tbody_tr:nth-child(3)]:select-none',
        )}
      >
        <DataGrid<GesellschafterItem>
          table={table}
          recordCount={visibleData.length}
          onRowClick={
            isAuthenticated
              ? (row) => {
                  if (row.gesellschafter_eu_id && row.register_court) {
                    window.location.href = generateCompanySlug(
                      {
                        name: row.name,
                        registerCourt: row.register_court,
                        registerType: row.register_type ?? undefined,
                        registerId: row.register_id ?? undefined,
                      },
                      locale,
                    );
                  }
                }
              : undefined
          }
          tableLayout={{
            headerSticky: true,
            headerBorder: true,
            rowBorder: true,
            stripped: false,
            dense: true,
            width: 'auto',
            columnsResizable: false,
            cellBorder: false,
          }}
          tableClassNames={{
            base: 'w-full',
            header: 'text-xs',
            headerRow: 'h-8 !bg-muted',
            body: 'text-xs text-foreground',
            bodyRow: 'h-auto hover:bg-muted/30 transition-colors',
          }}
        >
          <div className="flex-1 min-h-0 overflow-auto">
            <DataGridTable />
          </div>
        </DataGrid>
      </DataGridContainer>
      {hasMore && (
        <div className="px-5 py-2 text-xs text-muted-foreground border-t border-border/40 text-center">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              trackSignupAndRedirect(pathname, 'gesellschafter', euId);
            }}
            className="text-primary hover:underline"
          >
            {t(
              'companyDetail.registerToSeeAllShareholders',
              'Sign in to see all {{count}} shareholders',
              { count: sortedGesellschafter.length },
            )}
          </a>
        </div>
      )}
    </div>
  );
}

/**
 * Table of Beteiligungen (subsidiaries). Rows link to the subsidiary's own page when
 * `register_court` is known. Guests see the first three rows with the third blurred,
 * plus a sign-in CTA showing the total count.
 */
function SubsidiariesTabContent({
  subsidiaries,
  isAuthenticated,
  euId,
}: {
  subsidiaries: SubsidiaryCompanyItem[];
  isAuthenticated: boolean;
  euId: string;
}) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const { locale } = useLocale();
  // Stable identity required — see the note on the Gesellschafter table: an
  // inline .slice() per render makes react-table's auto-reset loop forever.
  const visibleData = useMemo(
    () => (isAuthenticated ? subsidiaries : subsidiaries.slice(0, 3)),
    [isAuthenticated, subsidiaries],
  );
  const hasMore = !isAuthenticated && subsidiaries.length > 2;

  const columns = useMemo<ColumnDef<SubsidiaryCompanyItem>[]>(
    () => [
      {
        id: 'company',
        header: t('companyDetail.participationCompany'),
        cell: (info) => {
          const p = info.row.original;
          const hasLink = Boolean(p.eu_id && p.register_court);
          const flagUrl = flagUrlForCountry(p.country_code);
          return (
            <div className="flex items-start gap-1.5">
              {flagUrl ? (
                <img
                  src={flagUrl}
                  alt={p.country_code ?? ''}
                  className="h-3.5 w-3.5 shrink-0 mt-0.5 rounded-sm object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    (
                      e.target as HTMLImageElement
                    ).nextElementSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              <Building2
                className={`h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5${flagUrl ? ' hidden' : ''}`}
              />
              {hasLink ? (
                <LocaleLink
                  href={generateCompanySlug(
                    {
                      name: p.name,
                      registerCourt: p.register_court!,
                      registerType: p.register_type ?? undefined,
                      registerId: p.register_id ?? undefined,
                    },
                    locale,
                  )}
                  className="text-xs hover:underline break-words whitespace-normal"
                  onClick={(e) => e.stopPropagation()}
                >
                  {p.name}
                </LocaleLink>
              ) : (
                <span className="text-xs break-words whitespace-normal">
                  {p.name}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'location',
        header: t('companyDetail.participationLocation'),
        cell: (info) => (
          <span className="text-xs break-words whitespace-normal">
            {info.row.original.sitz || '—'}
          </span>
        ),
        size: 100,
      },
    ],
    [t],
  );

  const table = useReactTable({
    data: visibleData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="relative">
      <DataGridContainer
        border={false}
        className={cn(
          'overflow-hidden flex flex-col min-w-0 [&_th:first-child]:pl-5 [&_td:first-child]:pl-5 [&_th:last-child]:pr-5 [&_td:last-child]:pr-5',
          isAuthenticated ? 'max-h-[320px]' : '',
          !isAuthenticated &&
            '[&_tbody_tr:nth-child(3)]:blur-[5px] [&_tbody_tr:nth-child(3)]:select-none',
        )}
      >
        <DataGrid<SubsidiaryCompanyItem>
          table={table}
          recordCount={visibleData.length}
          onRowClick={
            isAuthenticated
              ? (row) => {
                  if (row.register_court) {
                    window.location.href = generateCompanySlug(
                      {
                        name: row.name,
                        registerCourt: row.register_court,
                        registerType: row.register_type ?? undefined,
                        registerId: row.register_id ?? undefined,
                      },
                      locale,
                    );
                  }
                }
              : undefined
          }
          tableLayout={{
            headerSticky: true,
            headerBorder: true,
            rowBorder: true,
            stripped: false,
            dense: true,
            width: 'auto',
            columnsResizable: false,
            cellBorder: false,
          }}
          tableClassNames={{
            base: 'w-full',
            header: 'text-xs',
            headerRow: 'h-8 !bg-muted',
            body: 'text-xs text-foreground',
            bodyRow: 'h-auto hover:bg-muted/30 transition-colors',
          }}
        >
          <div className="flex-1 min-h-0 overflow-auto">
            <DataGridTable />
          </div>
        </DataGrid>
      </DataGridContainer>
      {hasMore && (
        <div className="px-5 py-2 text-xs text-muted-foreground border-t border-border/40 text-center">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              trackSignupAndRedirect(pathname, 'subsidiaries', euId);
            }}
            className="text-primary hover:underline"
          >
            {t(
              'companyDetail.registerToSeeAllSubsidiaries',
              'Sign in to see all {{count}} subsidiaries',
              { count: subsidiaries.length },
            )}
          </a>
        </div>
      )}
    </div>
  );
}
